import { FC } from "react";
import { Box, Tooltip, Typography } from "@material-ui/core";
import { FormikBag, FormikProps, withFormik } from "formik";
import * as yup from "yup";

import {
  Button,
  LabelField,
  MarketplaceMedianLabelField,
  NumberField,
  UsdIntegerInput,
} from "components";
import { getTrailingYield } from "lib/finance";
import { formatCurrencyAmount } from "lib/format";
import { parseIntegerAmount, parseNumber } from "lib/parse";
import { BaseAmountSource } from "services/listings/types";
import { ListingOffer } from "services/offers/types";
import theme from "theme";

interface FormValues {
  offerAmount: string;
  proxyAmount: string;
}

interface FormProps {
  buyNowPrice: number;
  highestOpenBuyerOfferAmount: number;
  initialValues: FormValues;
  isAcceptingFinalOffers: boolean;
  isAcceptingFinalOffersEnabled: boolean;
  isMarketplaceMedianDisabled: boolean;
  listingProxyIncrementAmount: number;
  ltm: number;
  marketplaceMedian: string | null;
  marketplaceMedianMultiplier: number;
  marketplaceMedianSource: BaseAmountSource;
  minimumPrice: number;
  onSave: (values: FormValues) => Promise<void>;
  onSaveSuccess: () => void;
  userActiveOffer: ListingOffer | null;
}

const MakeOfferForm: FC<FormProps & FormikProps<FormValues>> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  handleSubmit,
  isSubmitting,
  highestOpenBuyerOfferAmount,
  isAcceptingFinalOffers,
  isAcceptingFinalOffersEnabled,
  isMarketplaceMedianDisabled,
  listingProxyIncrementAmount,
  ltm,
  marketplaceMedian,
  marketplaceMedianMultiplier,
  marketplaceMedianSource,
  userActiveOffer,
}) => {
  const offerAmount = parseNumber(values.offerAmount, 0);
  const proxyAmount = parseNumber(values.proxyAmount, 0);
  const offerAmountPlaceholder = !!highestOpenBuyerOfferAmount
    ? formatCurrencyAmount(
        "USD",
        Number(highestOpenBuyerOfferAmount) +
          Number(listingProxyIncrementAmount),
      )
    : !!marketplaceMedian
    ? formatCurrencyAmount("USD", marketplaceMedian)
    : "";

  return (
    <form onSubmit={handleSubmit}>
      <Typography component="div" gutterBottom>
        <Box fontSize={13}>
          {isAcceptingFinalOffersEnabled
            ? "Enter an offer for this listing. You may also enter a proxy amount, which will automatically increase your offer to outbid other investors. You may edit or remove your proxy amount but your offers cannot be withdrawn."
            : "Enter an initial offer for this listing. Optionally, you can enter a proxy amount, and your offer will automatically increase to outbid other investors. You can edit or remove your proxy amount but your offer cannot be withdrawn."}
        </Box>
      </Typography>
      {isAcceptingFinalOffersEnabled && (
        <Typography component="div" gutterBottom>
          <Box fontSize={13}>
            {isAcceptingFinalOffers
              ? "If you are the highest bidder at the end of the auction period, you will win this asset. Please note, any bids in the final 5 minutes of the auction will reset the auction timer to 5 minutes."
              : "If the seller accepts an offer, a 24-hour auction will begin starting at the accepted amount. If no other investors bid, you will win this asset at the accepted price. You will have the opportunity to bid again during the final auction."}
          </Box>
        </Typography>
      )}
      <Box my={2} textAlign="center">
        {!isMarketplaceMedianDisabled && (
          <Box my={2}>
            <MarketplaceMedianLabelField
              marketplaceMedian={marketplaceMedian}
              marketplaceMedianMultiplier={marketplaceMedianMultiplier}
              marketplaceMedianSource={marketplaceMedianSource}
            />
          </Box>
        )}
        <Box my={2}>
          <Tooltip
            title={
              !!userActiveOffer && isAcceptingFinalOffers
                ? "You already have the current highest offer on this listing."
                : ""
            }
          >
            <span>
              <NumberField
                name="offerAmount"
                type="text"
                fullWidth
                label="Initial Offer Amount*"
                placeholder={offerAmountPlaceholder}
                value={parseIntegerAmount(values.offerAmount)}
                helperText={
                  touched.offerAmount && !!errors.offerAmount
                    ? errors.offerAmount
                    : `Trailing Earning Yield: ${getTrailingYield(
                        ltm,
                        offerAmount,
                      )}%`
                }
                disabled={
                  isSubmitting || (!!userActiveOffer && isAcceptingFinalOffers)
                }
                onBlur={handleBlur}
                onChange={handleChange}
                error={touched.offerAmount && !!errors.offerAmount}
                inputComponent={UsdIntegerInput}
              />
            </span>
          </Tooltip>
        </Box>
        <Box my={2}>
          <NumberField
            name="proxyAmount"
            type="text"
            fullWidth
            label="Proxy Amount"
            tooltipText={
              <>
                <Box>
                  This is the maximum amount you will offer on this listing.
                  This proxy will compete against other bidders but will not
                  interact with the listing owner’s counteroffers. (You will
                  need to manually interact with those.)
                </Box>
                <Box>
                  Your proxy will immediately outbid other investor’s offers. If
                  your offer is rejected or expires, an offer one increment
                  higher will be placed the following day. This will continue
                  until an offer is accepted or your proxy amount is reached.{" "}
                  <span
                    style={{ fontWeight: theme.typography.fontWeightMedium }}
                  >
                    Offers placed by your proxy can not be withdrawn.
                  </span>
                </Box>
              </>
            }
            placeholder=""
            value={parseIntegerAmount(values.proxyAmount)}
            helperText={
              touched.proxyAmount && !!errors.proxyAmount
                ? errors.proxyAmount
                : `Trailing Earning Yield: ${getTrailingYield(
                    ltm,
                    proxyAmount,
                  )}%`
            }
            disabled={isSubmitting}
            onBlur={handleBlur}
            onChange={handleChange}
            error={touched.proxyAmount && !!errors.proxyAmount}
            inputComponent={UsdIntegerInput}
          />
        </Box>
        {values.proxyAmount && (
          <Box my={2}>
            <LabelField
              label="Increase Increment"
              value={formatCurrencyAmount("USD", listingProxyIncrementAmount)}
              tooltipText="Your offer amount will increase by this amount."
            />
          </Box>
        )}
        <Box my={2}>
          <Button
            variant="contained"
            customColor="green"
            size="large"
            type="submit"
            fullWidth
            disabled={isSubmitting}
            id="send-offer"
          >
            Send Offer
          </Button>
        </Box>
      </Box>
    </form>
  );
};

const formikOptions = {
  mapPropsToValues: (props: FormProps) => props.initialValues,

  validationSchema: (props: FormProps) => {
    const {
      buyNowPrice,
      highestOpenBuyerOfferAmount,
      minimumPrice,
      userActiveOffer,
      isAcceptingFinalOffers,
      listingProxyIncrementAmount,
    } = props;

    return yup.object().shape({
      offerAmount: yup
        .string()
        .test(
          "offerGreaterThanListPrice",
          "The offer amount can't be greater than the list price",
          amount =>
            !amount || parseNumber(amount, 0) <= (buyNowPrice || Infinity),
        )
        .test(
          "greaterThanHighestOpenBuyerOfferAmountAndMinimumIncrement",
          `The offer amount must be greater than or equal to ${formatCurrencyAmount(
            "USD",
            highestOpenBuyerOfferAmount + listingProxyIncrementAmount,
          )} (the highest open offer + the minimum increment of ${formatCurrencyAmount(
            "USD",
            listingProxyIncrementAmount,
          )})`,
          amount =>
            !isAcceptingFinalOffers ||
            !amount ||
            !!userActiveOffer ||
            parseNumber(amount, 0) >=
              highestOpenBuyerOfferAmount + listingProxyIncrementAmount,
        )
        .test(
          "greaterThanHighestOpenBuyerOfferAmount",
          `The offer amount must be greater than the highest open offer of ${formatCurrencyAmount(
            "USD",
            highestOpenBuyerOfferAmount,
          )}`,
          amount =>
            !amount ||
            parseNumber(amount, 0) > highestOpenBuyerOfferAmount ||
            !!userActiveOffer,
        )
        .test(
          "smallerThanListingMinimumPrice",
          `The offer amount must be greater than listing minimum bid of ${formatCurrencyAmount(
            "USD",
            minimumPrice,
          )}`,
          amount => !amount || parseNumber(amount, 0) > minimumPrice,
        )
        .required("Required"),
      proxyAmount: yup
        .string()
        .test(
          "proxyGreaterThanListPrice",
          `The proxy amount must be less than the list price (${formatCurrencyAmount(
            "USD",
            buyNowPrice,
          )})`,
          amount =>
            !amount || parseNumber(amount, 0) < (buyNowPrice || Infinity),
        )
        .test(
          "greaterThanOfferAmount",
          "The proxy amount must be greater than the offer amount",
          function (proxyAmount) {
            const userActiveOfferIsHighest =
              highestOpenBuyerOfferAmount &&
              parseNumber(userActiveOffer?.amount, 0) ===
                highestOpenBuyerOfferAmount;
            return (
              (!proxyAmount && !userActiveOfferIsHighest) ||
              parseNumber(proxyAmount, 0) >
                parseNumber(this.parent.offerAmount, 0)
            );
          },
        )
        .test(
          "smallerThanListingMinimumPrice",
          `The proxy amount must be greater than listing minimum bid of ${formatCurrencyAmount(
            "USD",
            minimumPrice,
          )}`,
          amount => !amount || parseNumber(amount, 0) > minimumPrice,
        ),
    });
  },

  handleSubmit: async (
    values: FormValues,
    { props, setSubmitting }: FormikBag<FormProps, FormValues>,
  ) => {
    try {
      await props.onSave(values);
    } catch {
      // pass
    } finally {
      setSubmitting(false);
      props.onSaveSuccess();
    }
  },
};

const EnhancedMakeOfferForm = withFormik<FormProps, FormValues>(formikOptions)(
  MakeOfferForm,
);

export default EnhancedMakeOfferForm;
